/**
 * "⤢ Enlarge": a large chart or table in the whole window — Shayna, 2026-10-05 (#64),
 * after the Scorecard's score table had it (#19).
 *
 * The block is not copied into an overlay: the SAME element is lifted to fill the
 * window, in place. So everything inside it keeps its state through the pop-out and
 * back — a table's sort, how many rows are loaded, the filters above it, the scroll —
 * because nothing remounts. A copy rendered in a separate overlay would start sorted
 * by default and lose its loaded rows, which is exactly what someone enlarging a table
 * to work in it does not want.
 *
 * Closing: the × in the header, Esc, or anything that takes the reader somewhere else —
 * a row that opens a person (any side panel opening announces itself, below) or a move
 * to another page. Those close the pop-out first, so the drill lands on the page rather
 * than under a full-window layer.
 *
 * Small tiles do not get one. It is for blocks where room changes what you can read.
 */
import { createContext, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { SIDE_PANEL_OPEN, panelCount, useInPanel } from './SidePanel'

const Ctx = createContext(false)

/** True inside an enlarged block — for a table to take the window's height, say. */
export const useEnlarged = () => useContext(Ctx)

/** The trigger, the same everywhere so it reads as one feature. */
export function EnlargeButton({
  onClick,
  label = 'Enlarge',
  compact = false,
}: {
  onClick: () => void
  label?: string
  /** Icon only — for a card header with no room for a word. */
  compact?: boolean
}) {
  return (
    <button
      type="button"
      className={compact ? 'enlarge-btn compact' : 'enlarge-btn'}
      onClick={onClick}
      title="Show in the whole window"
      aria-label={compact ? `${label} — show in the whole window` : undefined}
    >
      <span aria-hidden="true">⤢</span>
      {!compact && ` ${label}`}
    </button>
  )
}

/**
 * A card's heading with the Enlarge button at its right. Enlarged, the pop-out's own
 * header carries the title, so the card's goes (`button` is null then, and so is this).
 */
export function CardHeading({ children, button }: { children: ReactNode; button: ReactNode }) {
  if (!button) return null
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
      <h3 className="card-heading" style={{ flex: 1, minWidth: 0 }}>
        {children}
      </h3>
      {button}
    </div>
  )
}

/** The enlarged view's own header: title, a muted note, any actions, and close. */
export function PopOutHead({
  title,
  note,
  actions,
  onClose,
}: {
  title: string
  note?: ReactNode
  actions?: ReactNode
  onClose: () => void
}) {
  return (
    <div className="popout-head">
      <h3 className="section-heading">{title}</h3>
      {note && <span className="popout-note">{note}</span>}
      <span style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
        {actions}
        <button type="button" className="popout-close" onClick={onClose} aria-label="Close">
          ×
        </button>
      </span>
    </div>
  )
}

/**
 * Esc closes, the page under it stops scrolling, and leaving — another page, or a
 * side panel opening — closes too. Shared by the in-place pop-out and ChartCard's.
 *
 * One Esc closes one layer. A dropdown inside the block handles its Esc on its own
 * element and marks it handled; this listener is on `document`, so it runs before a
 * side panel's (on `window`) and marks the key handled for it in turn. A panel opened
 * OVER the pop-out (the comments drawer) is newer, so its Esc comes first.
 */
export function usePopOutLifecycle(open: boolean, close: () => void) {
  const { pathname } = useLocation()
  const at = useRef(pathname)
  const closeRef = useRef(close)
  closeRef.current = close

  useEffect(() => {
    if (!open) return
    at.current = pathname
    const panelsUnder = panelCount()
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented || panelCount() > panelsUnder) return
      e.preventDefault()
      closeRef.current()
    }
    const onPanel = () => closeRef.current()
    document.addEventListener('keydown', onKey)
    window.addEventListener(SIDE_PANEL_OPEN, onPanel)
    const overflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      window.removeEventListener(SIDE_PANEL_OPEN, onPanel)
      document.body.style.overflow = overflow
    }
    // pathname is read, not watched, here: the effect below watches it.
  }, [open])

  useEffect(() => {
    if (open && pathname !== at.current) closeRef.current()
  }, [open, pathname])
}

/**
 * Wraps a large block. `children` is a function so the block can place the Enlarge
 * button in its own header row and size itself to the window when enlarged:
 *
 *   <PopOut title="Claim detail">
 *     {({ button, enlarged }) => <>…{button}…<DataTable maxHeight={enlarged ? … : '72vh'} /></>}
 *   </PopOut>
 */
export function PopOut({
  title,
  note,
  children,
  style,
}: {
  title: string
  note?: ReactNode
  children: (p: { button: ReactNode; enlarged: boolean; close: () => void }) => ReactNode
  /** On the block's own box — margins, a grid cell's sizing. */
  style?: CSSProperties
}) {
  const [enlarged, setEnlarged] = useState(false)
  const holder = useRef<HTMLDivElement>(null)
  // While lifted out, the holder keeps the block's height so the page under it does
  // not reflow and the reader comes back to the same scroll position.
  const [height, setHeight] = useState<number | null>(null)
  const close = () => setEnlarged(false)
  usePopOutLifecycle(enlarged, close)

  const open = () => {
    setHeight(holder.current?.getBoundingClientRect().height ?? null)
    setEnlarged(true)
  }
  useLayoutEffect(() => {
    if (!enlarged) setHeight(null)
  }, [enlarged])

  const inPanel = useInPanel()
  const button = enlarged || inPanel ? null : <EnlargeButton onClick={open} />

  return (
    <div ref={holder} style={{ ...style, ...(enlarged && height !== null ? { height } : null) }}>
      <div
        className={enlarged ? 'popout is-open' : 'popout'}
        role={enlarged ? 'dialog' : undefined}
        aria-modal={enlarged || undefined}
        aria-label={enlarged ? title : undefined}
      >
        {enlarged && <PopOutHead title={title} note={note} onClose={close} />}
        <Ctx.Provider value={enlarged}>{children({ button, enlarged, close })}</Ctx.Provider>
      </div>
    </div>
  )
}
